<i18n locale="en" lang="json">
{
  "title": "{appTitle} will automatically log you out in {seconds} seconds",
  "message": "The portal has not logged any activity from you for a long time and for security reasons it will automatically log you out. Would you like to continue?",
  "continue": "Continue"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "title": "{appTitle} gaat u automatisch uitloggen over {seconds} seconden",
  "message": "Het portaal heeft lange tijd geen activiteit meer geregistreerd van u en uit veiligheidsoverwegingen zal het u automatisch uitloggen. Wilt u toch doorgaan?",
  "continue": "Doorgaan"
}
</i18n>

<template>
  <v-dialog
    :model-value="isOpen"
    max-width="720"
    @click:outside="clickContinue"
  >
    <v-card v-set-test-id="['BaseInactivityTimer', testId]">
      <v-card-title>
        {{ t('title', { seconds: expireSeconds, appTitle: t('appTitle') }) }}
      </v-card-title>
      <v-card-text>{{ t('message') }}</v-card-text>
      <v-card-actions>
        <v-spacer />
        <base-button primary :icon="faCheck" @click="clickContinue">
          {{ t('continue') }}
        </base-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { useI18n } from 'vue-i18n';
import throttle from '@/common/throttle';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import { vSetTestId } from '@/common/components/ActionableComponent';

enum MessageType {
  warning,
  activity,
  continue,
}

type Props = ActionableComponentProps & {
  warningDelay: number;
  expireDelay: number;
};
const props = defineProps<Props>();
type Emits = { (e: 'expire' | 'continue' | 'heart-beat'): void };
const emit = defineEmits<Emits>();

const { t } = useI18n();

type TimeoutId = ReturnType<typeof setTimeout>;
type IntervalId = ReturnType<typeof setInterval>;
const timeoutId = ref<TimeoutId>();
const countdownIntervalId = ref<IntervalId>();
const heartBeatIntervalId = ref<IntervalId>();
const expireSeconds = ref(0);

const isOpen = ref(false);
const channelName = 'activityZvdZO';
const sendChannel = new BroadcastChannel(channelName);
const listenChannel = new BroadcastChannel(channelName);

const eventNames: string[] = [
  'mousemove',
  'keydown',
  'wheel',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'MSPointerDown',
  'MSPointerMove',
];

const privateSetTimeout = (func: () => void, delay: number) => {
  timeoutId.value = setTimeout(func, delay);
};

const startCountdown = () => {
  expireSeconds.value = Math.floor(props.expireDelay / 1000);
  countdownIntervalId.value = setInterval(() => {
    expireSeconds.value -= 1;
  }, 1000);
};

const expire = () => {
  clearInterval(countdownIntervalId.value);
  clearInterval(heartBeatIntervalId.value);
  expireSeconds.value = 0;
  isOpen.value = false;
  emit('expire');
};

const startExpireTimeout = () => {
  privateSetTimeout(() => expire(), props.expireDelay);
  startCountdown();
};

const startWarningTimeout = () => {
  privateSetTimeout(
    () => sendChannel.postMessage(MessageType.warning),
    props.warningDelay,
  );
};

const clickContinue = () => {
  sendChannel.postMessage(MessageType.continue);
};

const onActivity = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  !isOpen.value && startWarningTimeout();
};
const handleActivity = throttle(onActivity, 1000, { trailing: false });
const broadcastActivity = throttle(
  () => {
    sendChannel.postMessage(MessageType.activity);
  },
  1000,
  { trailing: false },
);

const handleMessage = (event: MessageEvent) => {
  if (event.data === MessageType.activity) {
    handleActivity();
  }
  if (event.data === MessageType.warning) {
    isOpen.value = true;
    startExpireTimeout();
  }
  if (event.data === MessageType.continue) {
    isOpen.value = false;
    clearInterval(countdownIntervalId.value);
    startWarningTimeout();
    emit('continue');
  }
};

watch(timeoutId, (_v: TimeoutId | undefined, oldId: TimeoutId | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  oldId && clearTimeout(oldId);
});

onMounted(() => {
  startWarningTimeout();
  listenChannel.addEventListener('message', handleMessage);

  eventNames.forEach(name =>
    document.body.addEventListener(name, broadcastActivity),
  );

  heartBeatIntervalId.value = setInterval(() => {
    emit('heart-beat');
  }, props.warningDelay / 3);
});

onUnmounted(() => {
  clearTimeout(timeoutId.value);
  clearInterval(countdownIntervalId.value);
  clearInterval(heartBeatIntervalId.value);
  eventNames.forEach(name =>
    document.body.removeEventListener(name, broadcastActivity),
  );
  listenChannel.removeEventListener('message', handleMessage);
});
</script>
